import { Flex } from "@chakra-ui/layout";
import { Select } from "@chakra-ui/react";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { appTheme } from "@styles/Theme";
import { formatShiftDate, formatShiftHours, transferTimeToDate } from "@utils/utils";
import { Button } from "../button/Button";
import Trash from "../icons/Trash";
import { EditLink } from "../text/EditLink";

// TODO: Phase 3 - Migrate DatePicker component
// For now, ShiftTime will work without the dayEditable feature
const DatePicker = ({ value, onChange }: any) => (
	<div style={{ padding: '1rem', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '5px' }}>
		<input
			type="date"
			value={value ? value.toISOString().split('T')[0] : ''}
			onChange={(e) => onChange(e.target.valueAsDate)}
			style={{ width: '100%', padding: '0.5rem', fontSize: '1rem' }}
		/>
	</div>
);

type TimeValue = {
	start: Date;
	end: Date;
};

interface Props {
	onChange: (value?: TimeValue) => any;
	value?: TimeValue;
	day?: Date;
	hideRemove?: boolean;
	dayEditable?: boolean;
	minHours?: number;
}

const timeslots = [
	//   "00",
	//   "01",
	//   "02",
	//   "03",
	//   "04",
	"05",
	"06",
	"07",
	"08",
	"09",
	"10",
	"11",
	"12",
	"13",
	"14",
	"15",
	"16",
	"17",
	"18",
	"19",
	"20",
	"21",
	"22",
	"23",
].flatMap((h) => [
	h + ":00",
	h + ":05",
	h + ":10",
	h + ":15",
	h + ":20",
	h + ":25",
	h + ":30",
	h + ":35",
	h + ":40",
	h + ":45",
	h + ":50",
	h + ":55",
]);

export const ShiftTime: React.FC<Props> = ({ onChange, value, hideRemove, dayEditable = false, day, minHours = 4 }) => {
	const [start, setStart] = useState<string>(value?.start ? formatShiftHours(value.start) : "");
	const [end, setEnd] = useState<string>(value?.end ? formatShiftHours(value.end) : "");
	const [error, setError] = useState<string>();
	const [selectedDay, setSelectedDay] = useState(value?.start || day);
	const [showDatePicker, setShowDatePicker] = useState(false);

	const validate = useCallback(
		(start?: string, end?: string) => {
			if (!start || !end) {
				setError("Ange både start och sluttid");
				return false;
			}

			const [fromHh, fromMm] = start.split(":");
			const [toHh, toMm] = end.split(":");

			const fromDate = dayjs().hour(Number(fromHh)).minute(Number(fromMm));
			const toDate = dayjs().hour(Number(toHh)).minute(Number(toMm));

			if (toDate.diff(fromDate, "hour") < minHours) {
				setError(`Arbetspass måste vara minst ${minHours} timmar`);
				return false;
			}
			setError("");

			return true;
		},
		[minHours],
	);

	const toValues = useMemo(() => {
		if (start) {
			const fromNumber = Number(start.replace(":", ""));
			return timeslots.filter((ts) => Number(ts.replace(":", "")) > fromNumber);
		}
		return timeslots;
	}, [start]);

	function clear() {
		setStart("");
		setEnd("");
		setError("");
		onChange(undefined);
	}

	function handleChangeTime(timeValue: string, type: "start" | "end") {
		if (type === "start") setStart(timeValue);
		if (type === "end") setEnd(timeValue);

		triggerOnChange(type === "start" ? timeValue : start, type === "end" ? timeValue : end, selectedDay!);
	}

	function handleChangeDay(newDay?: Date) {
		if (newDay) {
			setSelectedDay(newDay);

			triggerOnChange(start, end, newDay);

			setTimeout(() => {
				setShowDatePicker(false);
			}, 200);
		}
	}

	function triggerOnChange(startHhMm: string, endHhMm: string, day: Date) {
		const valid = validate(startHhMm, endHhMm);

		onChange(
			valid
				? {
						start: transferTimeToDate(startHhMm, day).toDate(),
						end: transferTimeToDate(endHhMm, day).toDate(),
					}
				: undefined,
		);
	}

	return (
		<Styles>
			<Flex align={"center"} m={"0.7rem 0 0.7rem 0.7rem"} w="100%" direction={"row"} justifyContent={"space-between"}>
				{dayEditable && (
					<div className="date-ctn">
						<EditLink onClick={() => setShowDatePicker(!showDatePicker)}>
							{formatShiftDate(selectedDay || new Date())}
						</EditLink>
						{showDatePicker && (
							<div className="popover-ctn">
								<DatePicker value={selectedDay} onChange={handleChangeDay} compact />
							</div>
						)}
					</div>
				)}
				<Flex gap={"0.7rem"} align={"center"}>
					<Select
						size="sm"
						maxW={"100px"}
						minW={"max-content"}
						onChange={(e) => handleChangeTime(e.target.value, "start")}
						value={start}
					>
						<option value="">--:--</option>
						{timeslots.map((t) => (
							<option key={t}>{t}</option>
						))}
					</Select>
					<span>till</span>
					<Select
						size="sm"
						maxW={"100px"}
						minW={"max-content"}
						onChange={(e) => handleChangeTime(e.target.value, "end")}
						value={end}
					>
						<option value="">--:--</option>
						{toValues.map((t) => (
							<option key={t}>{t}</option>
						))}
					</Select>
				</Flex>

				{!hideRemove && !error && start && end && (
					<Button
						icon={() => <Trash size={14} color={appTheme.colors.text} />}
						type="outline"
						size="s"
						title="Ta bort"
						onClick={clear}
						disabled={!!error || !start || !end}
						className="remove-btn"
					/>
				)}
			</Flex>
			{error && <div className="error">{error}</div>}
		</Styles>
	);
};

const Styles = styled.div`
	width: 100%;

	.error {
		margin-top: 0.35rem;
		color: ${(props) => props.theme.colors.destructive};
		font-size: ${(props) => props.theme.font.xSmall};
	}
	.remove-btn {
		min-width: 75px;
		margin-left: 0.5rem;
	}

	.date-ctn {
		position: relative;
		width: 100%;

		.popover-ctn {
			top: 35px;
			position: absolute;
			z-index: 10;
		}
	}
`;
